import React from 'react';
import { 
  Typography, 
  Box, 
  Grid,
  Paper, 
  Card, 
  CardContent, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';

import { useAuth } from '../../../contexts/AuthContext';

const KitchenDashboard: React.FC = () => {
  const { userProfile } = useAuth();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Kitchen Administration
      </Typography>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Welcome, {userProfile?.firstName || 'Admin'}
      </Typography>
      <Grid container spacing={3}>
        {/* Quick stats */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Kitchen Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: 'info.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3">42</Typography>
                    <Typography variant="subtitle1">Menu Items</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: 'warning.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3">8</Typography>
                    <Typography variant="subtitle1">Low Ingredients</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3">$3.8K</Typography>
                    <Typography variant="subtitle1">Weekly Sales</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Popular Menu Items
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <List>
                    <ListItem divider>
                      <Grid container alignItems="center">
                        <Grid item xs={8}>
                          <ListItemText 
                            primary="Lake Tahoe Burger" 
                            secondary="House patty, special sauce, lettuce, cheese" 
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">87 orders</Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Chip label="Top Seller" color="success" size="small" />
                        </Grid>
                      </Grid>
                    </ListItem>
                    <ListItem divider>
                      <Grid container alignItems="center">
                        <Grid item xs={8}>
                          <ListItemText 
                            primary="Marina Fish Tacos" 
                            secondary="Fresh catch, cabbage slaw, chipotle aioli" 
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">72 orders</Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Chip label="Popular" color="primary" size="small" />
                        </Grid>
                      </Grid>
                    </ListItem>
                    <ListItem divider>
                      <Grid container alignItems="center">
                        <Grid item xs={8}>
                          <ListItemText 
                            primary="Mountain Club Sandwich" 
                            secondary="Turkey, bacon, avocado, lettuce, tomato" 
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">65 orders</Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Chip label="Popular" color="primary" size="small" />
                        </Grid>
                      </Grid>
                    </ListItem>
                    <ListItem divider>
                      <Grid container alignItems="center">
                        <Grid item xs={8}>
                          <ListItemText 
                            primary="Fallen Leaf Salad" 
                            secondary="Mixed greens, dried cranberries, goat cheese, pine nuts" 
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">53 orders</Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Chip label="Healthy" color="secondary" size="small" />
                        </Grid>
                      </Grid>
                    </ListItem>
                    <ListItem>
                      <Grid container alignItems="center">
                        <Grid item xs={8}>
                          <ListItemText 
                            primary="Boat House Nachos" 
                            secondary="Tortilla chips, cheese, beans, jalapeños, sour cream" 
                          />
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Typography variant="body2">49 orders</Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: 'right' }}>
                          <Chip label="Shareable" color="info" size="small" />
                        </Grid>
                      </Grid>
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Recent activity */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Kitchen Alerts
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              <ListItem divider>
                <ListItemText 
                  primary="Low Stock: Avocados" 
                  secondary="Only 5 remaining" 
                  primaryTypographyProps={{ color: 'error.main' }}
                />
              </ListItem>
              <ListItem divider>
                <ListItemText 
                  primary="Low Stock: Gluten-free Buns" 
                  secondary="Only 8 remaining" 
                  primaryTypographyProps={{ color: 'error.main' }}
                />
              </ListItem>
              <ListItem divider>
                <ListItemText 
                  primary="New Delivery: Fresh Produce" 
                  secondary="Arrived July 6" 
                  primaryTypographyProps={{ color: 'success.main' }}
                />
              </ListItem>
              <ListItem divider>
                <ListItemText 
                  primary="Menu Item Update: Vegan Options" 
                  secondary="Added 2 new items" 
                  primaryTypographyProps={{ color: 'info.main' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Equipment Maintenance" 
                  secondary="Freezer service scheduled" 
                  primaryTypographyProps={{ color: 'warning.main' }}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
        
        {/* Quick links */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quick Links
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="subtitle1" color="primary">
                      Update Menu
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="subtitle1" color="primary">
                      Manage Inventory
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="subtitle1" color="primary">
                      Staff Schedule
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent>
                    <Typography variant="subtitle1" color="primary">
                      Sales Reports
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default KitchenDashboard; 